'use client';

import type { SchedulingAvailabilityInfo } from '@carvana/merch-contracts';
import { FulfillmentTypeKey } from '@carvana/merch-contracts/FulfillmentType';
import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import { useContext } from 'react';
import CommonStaticContext, {
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import fetchVehiclesDeliveryPickup from '~/services/common/fetchVehiclesDeliveryPickup';
import getSoonestRoute from '~/utilities/getSoonestRoute';
import { generateErrorLogger } from '~/utilities/loggers';

type DeliveryPickupState = {
  delivery: SchedulingAvailabilityInfo | undefined;
  pickup: SchedulingAvailabilityInfo | undefined;
};

type OwnArgs = {
  skipAndReset?: boolean;
  vehicleInventoryType: FulfillmentTypeKey;
  vehicleLocationId: number;
  vehicleSaleStatus: string;
};

const DEFAULT_STATE: DeliveryPickupState = {
  delivery: undefined,
  pickup: undefined
};

export default function useNewCarDeliveryPickup(
  vehicleId: number,
  {
    skipAndReset,
    vehicleInventoryType,
    vehicleLocationId,
    vehicleSaleStatus
  }: OwnArgs
) {
  const { clientId } = useContext(CommonStaticContext);
  const correlationId = useEventCorrelationId();
  const userId = useUserId();
  const { zip } = useUserLocation();

  const { data, isLoaded, isLoading, setData, setError, setLoading } =
    useLoadableState<DeliveryPickupState>(DEFAULT_STATE);

  useEffectWithController(
    controller => {
      if (skipAndReset) {
        setData(DEFAULT_STATE);

        return;
      }

      if (!zip || !vehicleId) return;

      setLoading();

      fetchVehiclesDeliveryPickup(
        {
          clientId: 'vdp_ui',
          correlationId,
          userId,
          vehicles: [
            {
              vehicleId,
              vehicleInventoryType,
              vehicleLocationId,
              vehicleSaleStatus
            }
          ],
          zip
        },
        controller
      )
        .then(({ data: response }) => {
          if (!isSuccess(response)) {
            throw new Error(response.errorMessage);
          }

          if (response.content.length === 0) {
            throw new Error('No content found');
          }

          const content = response.content[0];

          setData({
            delivery: isSuccess(content.soonestDelivery)
              ? content.soonestDelivery.content
              : undefined,
            pickup: isSuccess(content.soonestPickup)
              ? content.soonestPickup.content
              : undefined
          });
        })
        .catch(
          generateErrorLogger(
            'Get new car delivery & pickup info',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      clientId,
      correlationId,
      setData,
      setError,
      setLoading,
      skipAndReset,
      userId,
      vehicleId,
      vehicleInventoryType,
      vehicleLocationId,
      vehicleSaleStatus,
      zip
    ]
  );

  return {
    delivery: data.delivery,
    isLoaded,
    isLoading,
    pickup: data.pickup,
    soonestRoute: getSoonestRoute(data.delivery, data.pickup)
  };
}
